/**
 * AnalyzeImagesModal - Dialog fuer Batch-Analyse mit Kosten-Vorschau
 *
 * Zeigt Scope-Auswahl, Zusammenfassung, Kontingent-Bar und Credit-Warnung.
 * Startet die Analyse und wechselt zum AnalysisProgressOverlay.
 */

import { useState } from 'react'
import { Lightbulb, ArrowRight, AlertTriangle, Cpu, Zap, CheckSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Progress,
} from '@/components/v2/ui'
import { useAnalysisEstimate, useStartAnalysis } from '@/hooks/useImageIntelligence'
import { AnalysisProgressOverlay } from './AnalysisProgressOverlay'
import type { AnalysisEstimate } from '@/lib/api/image-intelligence'

export interface AnalyzeImagesModalProps {
  /** Dialog open state */
  open: boolean
  /** Dialog open change handler */
  onOpenChange: (open: boolean) => void
  /** Called when analysis completes */
  onAnalysisComplete?: () => void
  /** When set, analyze only these products (selection mode) */
  productIds?: string[]
}

export function AnalyzeImagesModal({
  open,
  onOpenChange,
  onAnalysisComplete,
  productIds,
}: AnalyzeImagesModalProps) {
  const { t } = useTranslation()
  const hasSelection = !!productIds?.length
  const [scope, setScope] = useState<'all' | 'missing_only'>('missing_only')
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false)
  const [autoAssignThreshold, setAutoAssignThreshold] = useState(75)

  const { data: estimate, isLoading } = useAnalysisEstimate(
    hasSelection ? 'all' : scope,
    hasSelection ? productIds : undefined,
  )
  const startMutation = useStartAnalysis()

  const handleStart = (): void => {
    startMutation.mutate(
      {
        scope: hasSelection ? 'all' : scope,
        autoAssignThreshold: autoAssignEnabled ? autoAssignThreshold : undefined,
        productIds: hasSelection ? productIds : undefined,
      },
      {
        onSuccess: (run) => {
          const runId = run.run_id || run.id
          if (runId) setActiveRunId(runId)
        },
      },
    )
  }

  const handleComplete = (): void => {
    setActiveRunId(null)
    onOpenChange(false)
    onAnalysisComplete?.()
  }

  if (activeRunId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <AnalysisProgressOverlay
            runId={activeRunId}
            onComplete={handleComplete}
            onCancel={() => setActiveRunId(null)}
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary-500" aria-hidden="true" />
            <DialogTitle>{t('readiness.startAnalysis')}</DialogTitle>
          </div>
          <DialogDescription>
            {estimate ? t('readiness.imagesWillBeAnalyzed', { count: estimate.to_analyze }) : t('readiness.loadingEstimate')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {hasSelection ? (
            <SelectedProductsInfo count={productIds.length} />
          ) : (
            <ScopeSelector scope={scope} onScopeChange={setScope} estimate={estimate} />
          )}

          <AutoAssignToggle
            enabled={autoAssignEnabled}
            threshold={autoAssignThreshold}
            onEnabledChange={setAutoAssignEnabled}
            onThresholdChange={setAutoAssignThreshold}
          />

          {estimate && !isLoading && (
            <EstimateSummary estimate={estimate} />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleStart}
            disabled={isLoading || startMutation.isPending || !estimate || estimate.to_analyze === 0}
            className="gap-1.5"
          >
            {startMutation.isPending ? t('readiness.startingAnalysis') : t('readiness.startAnalysisButton')}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function ScopeSelector({ scope, onScopeChange, estimate }: {
  scope: string
  onScopeChange: (s: 'all' | 'missing_only') => void
  estimate: { to_analyze: number; unique_images: number } | undefined
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {t('readiness.scope')}
      </label>
      <div className="space-y-2">
        <ScopeOption
          active={scope === 'missing_only'}
          onClick={() => onScopeChange('missing_only')}
          title={t('readiness.missingPackshotOnly')}
          description={estimate ? t('readiness.imagesWillBeAnalyzedCount', { count: estimate.to_analyze }) : t('readiness.loadingEstimate')}
        />
        <ScopeOption
          active={scope === 'all'}
          onClick={() => onScopeChange('all')}
          title={t('readiness.reanalyzeAll')}
          description={estimate ? t('readiness.imagesWillBeAnalyzedCount', { count: estimate.unique_images }) : t('readiness.loadingEstimate')}
        />
      </div>
    </div>
  )
}

function ScopeOption({ active, onClick, title, description }: {
  active: boolean; onClick: () => void; title: string; description: string
}) {
  return (
    <label
      className={`analyze-modal-scope-option flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        active
          ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
          : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
      }`}
      onClick={onClick}
    >
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
        active ? 'border-primary-500' : 'border-neutral-300 dark:border-neutral-600'
      }`}>
        {active && <div className="w-2 h-2 rounded-full bg-primary-500" />}
      </div>
      <div>
        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{title}</div>
        <div className="text-xs text-neutral-500">{description}</div>
      </div>
    </label>
  )
}

function SelectedProductsInfo({ count }: { count: number }) {
  const { t } = useTranslation()
  return (
    <div className="analyze-modal-selection flex items-center gap-2 p-3 rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20">
      <CheckSquare className="h-4 w-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
        {t('readiness.analyzeSelected')} ({count})
      </span>
    </div>
  )
}

function AutoAssignToggle({ enabled, threshold, onEnabledChange, onThresholdChange }: {
  enabled: boolean
  threshold: number
  onEnabledChange: (v: boolean) => void
  onThresholdChange: (v: number) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
        />
        <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
          {t('readiness.autoAssignPackshot')}
        </span>
      </label>
      {enabled && (
        <div className="ml-6 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-500">{t('readiness.minScore')}</span>
            <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300 tabular-nums">
              {threshold}
            </span>
          </div>
          <input
            type="range"
            min={60}
            max={95}
            step={5}
            value={threshold}
            onChange={(e) => onThresholdChange(Number(e.target.value))}
            className="w-full accent-primary-500"
          />
          <p className="text-xs text-neutral-500">
            {t('readiness.autoAssignHint', { threshold })}
          </p>
        </div>
      )}
    </div>
  )
}

function EstimateSummary({ estimate }: { estimate: AnalysisEstimate }) {
  const { t } = useTranslation()
  const overQuota = !estimate.quota_sufficient
  const overage = Math.max(0, estimate.to_analyze - estimate.quota_remaining)
  const afterAnalysis = Math.max(0, estimate.quota_remaining - estimate.to_analyze)
  // Progress zeigt: wie viel Prozent des Kontingents wird durch diese Analyse verbraucht
  const quotaPercent = estimate.quota_remaining > 0
    ? Math.min(100, Math.round((estimate.to_analyze / estimate.quota_remaining) * 100))
    : 100

  return (
    <div className="analyze-modal-summary rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-4 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {t('readiness.summary')}
      </h4>
      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
        <span className="text-neutral-500">{t('readiness.imagesToAnalyze')}</span>
        <span className="text-right font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">
          {estimate.to_analyze}
        </span>
        <span className="text-neutral-500">{t('readiness.alreadyAnalyzed')}</span>
        <span className="text-right font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">
          {estimate.already_analyzed}
        </span>
      </div>
      <QuotaBar
        toAnalyze={estimate.to_analyze}
        afterAnalysis={afterAnalysis}
        quotaRemaining={estimate.quota_remaining}
        quotaPercent={quotaPercent}
        overQuota={overQuota}
      />
      {overQuota && overage > 0 && (
        <OverageWarning overage={overage} credits={estimate.estimated_credits} />
      )}
      <div className="analyze-modal-model flex items-center justify-between text-xs pt-1">
        <span className="text-neutral-500">Model</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 font-medium">
          <Cpu className="h-3 w-3" aria-hidden="true" />
          GPT-5 Nano
        </span>
      </div>
    </div>
  )
}

function QuotaBar({ toAnalyze, afterAnalysis, quotaRemaining, quotaPercent, overQuota }: {
  toAnalyze: number
  afterAnalysis: number
  quotaRemaining: number
  quotaPercent: number
  overQuota: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="analyze-modal-quota pt-2 border-t border-neutral-200 dark:border-neutral-700 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">{t('readiness.quotaConsumedBy')}</span>
        <span className="text-xs font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {toAnalyze}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">{t('readiness.quotaRemainingAfter')}</span>
        <span className={`text-xs font-medium tabular-nums ${
          afterAnalysis === 0 ? 'text-red-500 dark:text-red-400' : 'text-neutral-700 dark:text-neutral-300'
        }`}>
          {afterAnalysis} / {quotaRemaining} {t('readiness.quotaAvailable')}
        </span>
      </div>
      <Progress value={quotaPercent} size="sm" variant={overQuota ? 'error' : 'default'} />
    </div>
  )
}

function OverageWarning({ overage, credits }: { overage: number; credits: number }) {
  const { t } = useTranslation()
  return (
    <div className="analyze-modal-overage flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="text-xs">
        <span className="font-medium text-amber-800 dark:text-amber-200">
          {t('readiness.overQuota', { count: overage })}
        </span>
        <span className="text-amber-700 dark:text-amber-300">
          {' '}{t('readiness.creditsDeducted', { count: credits })}
        </span>
      </div>
    </div>
  )
}
